import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { collection, query, orderBy, getDocs, addDoc, Timestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { X, ChevronUp, Calendar, MessageSquare, Loader2, Send, Trash2, Edit2 } from 'lucide-react';
import { getNicknameInitials } from '../utils/nicknameGenerator';
import Markdown from './Markdown';

// Helper function to format status for display
const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  createdBy: string;
  createdByEmail: string;
  createdByNickname?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  imageUrls?: string[];
}

interface Comment {
  id: string;
  userId: string;
  userEmail: string;
  userNickname: string;
  text: string;
  createdAt: Timestamp;
  editedAt?: Timestamp;
}

interface RequestDetailModalProps {
  request: FeatureRequest;
  customerId: string;
  brandColor: string;
  hasVoted: boolean;
  onClose: () => void;
  onVote: (requestId: string) => void;
}

export default function RequestDetailModal({
  request,
  customerId,
  brandColor,
  hasVoted,
  onClose,
  onVote,
}: RequestDetailModalProps) {
  const { user, userProfile } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    const fetchComments = async () => {
      setLoadingComments(true);
      try {
        const commentsRef = collection(db, 'customers', customerId, 'requests', request.id, 'comments');
        const q = query(commentsRef, orderBy('createdAt', 'asc'));
        const snapshot = await getDocs(q);

        const commentsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Comment[];

        setComments(commentsList);
        setCommentCount(commentsList.length);
      } catch (err) {
        console.error('Error fetching comments:', err);
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
  }, [customerId, request.id]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !userProfile) {
      window.location.href = '/login';
      return;
    }

    if (!commentText.trim()) return;

    setSubmittingComment(true);
    try {
      await addDoc(
        collection(db, 'customers', customerId, 'requests', request.id, 'comments'),
        {
          userId: user.uid,
          userEmail: user.email || 'Anonymous',
          userNickname: userProfile.nickname,
          customerId: customerId,
          text: commentText.trim(),
          createdAt: Timestamp.now(),
        }
      );

      // Refetch comments to ensure consistency
      const commentsRef = collection(db, 'customers', customerId, 'requests', request.id, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);

      const commentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Comment[];

      setComments(commentsList);
      setCommentCount(commentsList.length);
      setCommentText('');
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;

    const confirmed = window.confirm('Are you sure you want to delete this comment?');
    if (!confirmed) return;

    setDeletingCommentId(commentId);
    try {
      await deleteDoc(doc(db, 'customers', customerId, 'requests', request.id, 'comments', commentId));

      // Update local state
      setComments(prev => prev.filter(c => c.id !== commentId));
      setCommentCount(prev => prev - 1);
    } catch (err) {
      console.error('Error deleting comment:', err);
      toast.error('Failed to delete comment. Please try again.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.text);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!user || !editText.trim()) return;

    try {
      await updateDoc(doc(db, 'customers', customerId, 'requests', request.id, 'comments', commentId), {
        text: editText,
        editedAt: Timestamp.now(),
      });

      // Update local state
      setComments(prev =>
        prev.map(c =>
          c.id === commentId
            ? { ...c, text: editText, editedAt: Timestamp.now() }
            : c
        )
      );

      setEditingCommentId(null);
      setEditText('');
    } catch (err) {
      console.error('Error editing comment:', err);
      toast.error('Failed to edit comment. Please try again.');
    }
  };

  const canEditComment = (comment: Comment): boolean => {
    if (!user || comment.userId !== user.uid) return false;

    // Check if comment was created within 5 minutes
    const now = Date.now();
    const commentTime = comment.createdAt.toDate().getTime();
    const fiveMinutes = 5 * 60 * 1000;

    return (now - commentTime) < fiveMinutes;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-start gap-4 mb-3">
              {/* Vote Button */}
              <button
                onClick={() => onVote(request.id)}
                className="w-12 h-12 rounded-lg border hover:border-primary flex items-center justify-center transition-colors flex-shrink-0"
                style={
                  hasVoted
                    ? {
                        backgroundColor: brandColor,
                        borderColor: brandColor,
                        color: 'white',
                      }
                    : {
                        borderColor: brandColor,
                        color: brandColor,
                      }
                }
              >
                <div className="flex flex-col items-center">
                  <ChevronUp className="h-5 w-5" />
                  <span className="text-xs font-semibold">{request.voteCount}</span>
                </div>
              </button>

              <div className="flex-1">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {request.title}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${brandColor}20`,
                      color: brandColor,
                    }}
                  >
                    {formatStatus(request.status)}
                  </span>
                  <span className="px-2 py-1 bg-muted rounded text-xs capitalize">
                    {request.category.replace('_', ' ')}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {new Date(request.createdAt.toDate()).toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MessageSquare className="h-3 w-3" />
                    {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Submitted by {request.createdByNickname || request.createdByEmail}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Description */}
        <div className="p-6 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground mb-2">Description</h3>
          <div className="text-muted-foreground">
            <Markdown content={request.description} />
          </div>

          {/* Image Attachments */}
          {request.imageUrls && request.imageUrls.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-foreground mb-2">Attachments</h4>
              <div className="grid grid-cols-2 gap-3">
                {request.imageUrls.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <img
                      src={url}
                      alt={`Attachment ${index + 1}`}
                      className="w-full h-48 object-cover rounded border border-border group-hover:opacity-80 transition-opacity cursor-pointer"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Comments ({commentCount})
          </h3>

          {/* Comment Form */}
          {user ? (
            <form onSubmit={handleSubmitComment} className="mb-6">
              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={submittingComment}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <Button
                  type="submit"
                  disabled={!commentText.trim() || submittingComment}
                  style={{ backgroundColor: brandColor }}
                  className="text-white"
                >
                  {submittingComment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Post Comment
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mb-6 p-4 border border-border rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground text-center">
                <button
                  onClick={() => (window.location.href = '/login')}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>{' '}
                to comment on this request
              </p>
            </div>
          )}

          {/* Comments List */}
          {loadingComments ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No comments yet</p>
              <p className="text-sm text-muted-foreground">Be the first to share your thoughts!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="border border-border rounded-lg p-4 bg-background"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: brandColor }}
                      >
                        {getNicknameInitials(comment.userNickname || comment.userEmail)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {comment.userNickname || 'Anonymous'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(comment.createdAt.toDate()).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {user && comment.userId === user.uid && (
                      <div className="flex gap-2">
                        {canEditComment(comment) && editingCommentId !== comment.id && (
                          <button
                            onClick={() => handleEditComment(comment)}
                            className="text-muted-foreground hover:text-primary transition-colors p-1"
                            title="Edit comment (within 5 minutes)"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          disabled={deletingCommentId === comment.id}
                          className="text-muted-foreground hover:text-destructive transition-colors p-1"
                          title="Delete comment"
                        >
                          {deletingCommentId === comment.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  {editingCommentId === comment.id ? (
                    <div className="pl-10 space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveEdit(comment.id)}
                          disabled={!editText.trim()}
                          style={{ backgroundColor: brandColor }}
                          className="text-white"
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingCommentId(null);
                            setEditText('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-foreground pl-10">
                      <Markdown content={comment.text} />
                      {comment.editedAt && (
                        <p className="text-xs text-muted-foreground mt-1 italic">
                          (edited)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
